/**
 * session.ts
 * Client-side auth session: the decoded CLAIMS only — never the token.
 *
 * SAME mechanism as apps/frontend/src/lib/session.ts (do not invent a new one,
 * per the scaffold brief): the JWT lives in the `suite_session` httpOnly cookie
 * set by the backend. JS — and therefore any XSS payload — CANNOT read it. The
 * browser attaches it automatically on every API call (credentials:'include').
 * This app never holds the token; it learns "who am I" from GET /auth/session,
 * which returns the non-sensitive claims (scope, roles, capabilities, exp) the
 * router guard and UI need. A reload re-hydrates them from the cookie via
 * hydrateSession().
 *
 * Opening the app with no signal (operator-dictation-queue): the last claims
 * the server confirmed are also kept in localStorage, and used ONLY when
 * GET /auth/session cannot be reached (or answers a passing 408/429/5xx),
 * and only while they are not expired — so a worker on the plateau can still record a dictation for the
 * queue. They are UI hints, never credentials (the token stays in the
 * httpOnly cookie, and the server checks it on every send). Signing out, or
 * a 401 from GET /auth/session, forgets them.
 *
 * This app is operator-facing (tenant users only), but GET /auth/session can
 * still return a PlatformClaims shape for a platform account by construction —
 * kept as a discriminated union for type-safety with the shared contract, even
 * though the operator UI only ever expects 'tenant'.
 */
import { reactive, computed, type ComputedRef } from 'vue';
import type { AuthSessionResponse } from '@suite/contracts-kernel';
import { getSession } from './api.js';
import { ApiError, isUnreachable } from './http.js';

export type SessionClaims = AuthSessionResponse;

interface SessionState {
  claims: SessionClaims | null;
}

const state = reactive<SessionState>({
  claims: null,
});

// ---------------------------------------------------------------------------
// Account-session (transient) — passes the short-lived account-session token
// from the choose-company login response to selectCompany(), then discarded
// immediately after. Mirrored into sessionStorage (2026-07-21): a bare module
// variable does not survive a page reload, and this app's PWA service worker
// (registerType:'autoUpdate') calls window.location.reload() the moment a new
// SW version activates — which can happen mid-flow (Cloudflare rebuilds this
// project on every push to main, unrelated ones included). For a
// multi-company account that reload silently stranded the user between
// "choose your company" and clicking one, surfacing as "Sesión de cuenta
// expirada" even though nothing was actually wrong. The server-side 5-minute
// TTL on this token already bounds the exposure window, so sessionStorage
// (cleared on tab close) doesn't meaningfully change the risk profile.
// ---------------------------------------------------------------------------

const ACCOUNT_SESSION_STORAGE_KEY = 'rigx_account_session';

let accountSessionToken: string | null = null;

const LAST_CLAIMS_STORAGE_KEY = 'rigx.session.last-claims';

function rememberClaims(claims: SessionClaims | null): void {
  try {
    if (claims) localStorage.setItem(LAST_CLAIMS_STORAGE_KEY, JSON.stringify(claims));
    else localStorage.removeItem(LAST_CLAIMS_STORAGE_KEY);
  } catch {
    // Storage blocked: offline opening simply falls back to the login.
  }
}

function lastKnownClaims(): SessionClaims | null {
  try {
    const raw = localStorage.getItem(LAST_CLAIMS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SessionClaims) : null;
  } catch {
    return null;
  }
}

export function setSessionClaims(claims: SessionClaims | null): void {
  state.claims = claims;
}

/**
 * Hydrate the session from the httpOnly cookie by asking the backend who we are.
 * Call once on app boot (before the router's first guard) and after login /
 * select-company (the cookie was just (re)set by the backend).
 *
 * Returns true when an authenticated, non-expired session was loaded. With no
 * connection, or a passing server problem (408, 429, 5xx), it falls back to
 * the last confirmed claims while they are still valid. A 401 forgets them;
 * any other rejection leaves the app signed out without forgetting them.
 * Signed out → the guard bounces to login.
 */
export async function hydrateSession(): Promise<boolean> {
  try {
    const claims = await getSession();
    state.claims = claims;
    rememberClaims(claims);
    return isAuthenticated.value;
  } catch (err) {
    const fallback = isUnreachable(err) ? lastKnownClaims() : null;
    state.claims = fallback && !isExpired(fallback) ? fallback : null;
    if (err instanceof ApiError && err.status === 401) rememberClaims(null);
    return isAuthenticated.value;
  }
}

export function clearSession(): void {
  state.claims = null;
  rememberClaims(null);
}

export function setAccountSession(token: string): void {
  accountSessionToken = token;
  try {
    sessionStorage.setItem(ACCOUNT_SESSION_STORAGE_KEY, token);
  } catch {
    // sessionStorage unavailable (private-browsing lockdown, etc.) — the
    // in-memory variable still covers the common case.
  }
}

export function clearAccountSession(): void {
  accountSessionToken = null;
  try {
    sessionStorage.removeItem(ACCOUNT_SESSION_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function getAccountSession(): string | null {
  if (accountSessionToken) return accountSessionToken;
  try {
    return sessionStorage.getItem(ACCOUNT_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** True when the token's exp is in the past (with a 10s skew margin). */
function isExpired(claims: SessionClaims | null): boolean {
  if (!claims) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return claims.exp <= nowSeconds + 10;
}

/** Reactive: authenticated == present, non-expired claims. */
export const isAuthenticated: ComputedRef<boolean> = computed(
  () => state.claims !== null && !isExpired(state.claims),
);

/** Reactive: current decoded claims (or null). */
export const currentClaims: ComputedRef<SessionClaims | null> = computed(() => state.claims);

// ---------------------------------------------------------------------------
// Keep-alive (fix/sliding-session). The backend renews a cookie session on
// use (sliding), but this app's in-memory claims keep the expiry they were
// loaded with. Coming back to the app, and every 15 minutes while it is on
// screen, GET /auth/session refreshes both: the server renews the cookie and
// answers the new expiry. A session the server no longer accepts is reported
// to `onLost` (→ login with a notice); no signal is not a lost session.
// ---------------------------------------------------------------------------

const KEEP_ALIVE_EVERY_MS = 15 * 60 * 1000;

export function startSessionKeepAlive(onLost: () => void): () => void {
  let checking = false;
  const check = async (): Promise<void> => {
    if (checking || document.visibilityState !== 'visible' || !isAuthenticated.value) return;
    checking = true;
    try {
      if (!(await hydrateSession())) onLost();
    } finally {
      checking = false;
    }
  };
  const onVisibility = (): void => {
    void check();
  };
  document.addEventListener('visibilitychange', onVisibility);
  const timer = setInterval(() => void check(), KEEP_ALIVE_EVERY_MS);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    clearInterval(timer);
  };
}
